import { useState, type FormEvent } from 'react';
import { demander } from '@/components/ui/Fenetres';
import { Paperclip, Plus } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/stores/toastStore';
import { useAuthStore } from '@/stores/authStore';
import { reduirePhoto } from '@/api/mainOeuvre';
import { pointageApi, presenceApi, type AbsenceJustifiee, type MotifAbsence } from '@/api/pointage';
import { LIBELLE_MOTIF } from './PresenceJour';

/**
 * Pointage P3 — absences justifiées (docs/PLAN-POINTAGE.md).
 * Un jour couvert n'est plus « Absent ». Une absence ne se supprime pas : elle s'annule avec un motif.
 */
const PDF_MAX_OCTETS = 3 * 1024 * 1024;
const dateFr = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'UTC' });
const lireEnBase64 = (f: File) => new Promise<string>((ok, ko) => {
  const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => ko(r.error); r.readAsDataURL(f);
});

export const AbsencesPointage = () => {
  const role = useAuthStore((s) => s.user?.role);
  const peutSaisir = role === 'rh' || role === 'daf' || role === 'admin_holding';
  const voitPieces = peutSaisir || role === 'dg_filiale';
  const qc = useQueryClient();
  const [avecAnnulees, setAvecAnnulees] = useState(false);
  const liste = useQuery({ queryKey: ['hr', 'pointage', 'absences', avecAnnulees], queryFn: () => presenceApi.absences({ avecAnnulees }) });
  const employes = useQuery({ queryKey: ['hr', 'pointage', 'employes'], queryFn: pointageApi.employes, enabled: peutSaisir });

  const vide = { employeeId: '', du: '', au: '', motif: 'conge' as MotifAbsence, commentaire: '' };
  const [f, setF] = useState(vide);
  const [piece, setPiece] = useState<{ path: string; nom: string } | null>(null);
  const [depot, setDepot] = useState(false);

  const joindre = async (fichier: File) => {
    setDepot(true);
    try {
      const pdf = fichier.type === 'application/pdf' || /\.pdf$/i.test(fichier.name);
      if (!pdf && !fichier.type.startsWith('image/')) { toast.error('Fichier refusé', 'Joignez une photo ou un PDF.'); return; }
      if (pdf && fichier.size > PDF_MAX_OCTETS) { toast.error('PDF trop lourd', 'Maximum 3 Mo. Photographiez plutôt la pièce.'); return; }
      const corps = pdf ? { dataBase64: await lireEnBase64(fichier), contentType: 'application/pdf' } : await reduirePhoto(fichier);
      const r = await presenceApi.deposerPiece({ ...corps, nomOrigine: fichier.name });
      setPiece({ path: r.path, nom: fichier.name });
    } finally { setDepot(false); }
  };

  const creer = useMutation({
    mutationFn: () => presenceApi.creerAbsence({
      employeeId: f.employeeId, du: f.du, au: f.au || f.du, motif: f.motif,
      commentaire: f.commentaire.trim() || undefined, piecePath: piece?.path,
    }),
    onSuccess: (a) => {
      toast.success('Absence enregistrée', `${dateFr(a.du)} → ${dateFr(a.au)}`);
      setF(vide); setPiece(null);
      void qc.invalidateQueries({ queryKey: ['hr', 'pointage'] });
    },
  });
  const annuler = useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) => presenceApi.annulerAbsence(id, motif),
    onSuccess: () => { toast.success('Absence annulée'); void qc.invalidateQueries({ queryKey: ['hr', 'pointage'] }); },
  });
  const ouvrirPiece = async (a: AbsenceJustifiee) => { window.open((await presenceApi.pieceAbsence(a.id)).url, '_blank', 'noopener'); };
  const demanderAnnulation = async (a: AbsenceJustifiee) => {
    const motif = await demander.texte({ titre: `Annuler l'absence de ${a.employee.fullName} ?`, message: `Du ${dateFr(a.du)} au ${dateFr(a.au)}.`, libelle: 'Motif', min: 5, confirmer: 'Annuler l’absence', danger: true });
    if (motif === null) return;
    annuler.mutate({ id: a.id, motif });
  };
  const soumettre = (e: FormEvent) => { e.preventDefault(); if (f.employeeId && f.du) creer.mutate(); };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {peutSaisir && (
        <div className="ds-table-panel">
          <div className="ds-table-header"><div className="ds-table-title">Justifier une absence</div></div>
          <form onSubmit={soumettre} style={{ padding: 16, display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <label style={{ fontSize: 12 }}>Employé
              <select className="ds-fi" value={f.employeeId} onChange={(e) => setF({ ...f, employeeId: e.target.value })} required>
                <option value="">— choisir —</option>
                {employes.data?.map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12 }}>Du<input type="date" className="ds-fi" value={f.du} onChange={(e) => setF({ ...f, du: e.target.value })} required /></label>
            <label style={{ fontSize: 12 }}>Au (inclus)<input type="date" className="ds-fi" value={f.au} min={f.du} onChange={(e) => setF({ ...f, au: e.target.value })} /></label>
            <label style={{ fontSize: 12 }}>Motif
              <select className="ds-fi" value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value as MotifAbsence })}>
                {Object.entries(LIBELLE_MOTIF).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12, gridColumn: '1 / -1' }}>Commentaire{f.motif === 'autre' && <span style={{ color: 'var(--danger)' }}> — obligatoire pour « Autre »</span>}
              <input className="ds-fi" value={f.commentaire} maxLength={500} onChange={(e) => setF({ ...f, commentaire: e.target.value })} required={f.motif === 'autre'} />
            </label>
            <div style={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, fontSize: 12 }}>
              <span><Paperclip size={12} style={{ verticalAlign: -2 }} /> Pièce (certificat, demande signée — photo ou PDF, facultatif) :</span>
              {piece ? (
                <><strong>{piece.nom}</strong><button type="button" className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setPiece(null)}>Retirer</button></>
              ) : (
                <input type="file" accept="image/*,application/pdf" disabled={depot} style={{ maxWidth: '100%' }}
                  onChange={(e) => { const fi = e.target.files?.[0]; e.target.value = ''; if (fi) void joindre(fi); }} />
              )}
              {depot && 'Dépôt en cours…'}
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="ds-btn ds-btn-p" type="submit" disabled={creer.isPending || depot}><Plus size={14} /> Enregistrer l'absence</button>
            </div>
          </form>
        </div>
      )}

      <div className="ds-table-panel">
        <div className="ds-table-header">
          <div className="ds-table-title">Absences justifiées</div>
          <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={avecAnnulees} onChange={(e) => setAvecAnnulees(e.target.checked)} /> Afficher les annulées
          </label>
        </div>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="ds-tbl" style={{ minWidth: 640 }}>
            <thead><tr><th>Employé</th><th style={{ width: 190 }}>Période</th><th style={{ width: 110 }}>Motif</th><th>Commentaire</th><th style={{ width: 150 }} /></tr></thead>
            <tbody>
              {liste.isLoading && <tr><td colSpan={5}>Chargement…</td></tr>}
              {liste.data?.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Aucune absence justifiée</td></tr>}
              {liste.data?.map((a) => (
                <tr key={a.id} style={a.annuleLe ? { opacity: 0.55 } : undefined}>
                  <td><strong>{a.employee.fullName}</strong>{a.annuleLe && <div style={{ fontSize: 11 }}>Annulée : {a.motifAnnulation}</div>}</td>
                  <td>{dateFr(a.du)} → {dateFr(a.au)}</td>
                  <td><span className="ds-bd ds-bd-i">{LIBELLE_MOTIF[a.motif]}</span></td>
                  <td style={{ fontSize: 12 }}>{a.commentaire ?? '—'}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    {voitPieces && a.piecePath && <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void ouvrirPiece(a)}><Paperclip size={12} /> Pièce</button>}
                    {peutSaisir && !a.annuleLe && <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => demanderAnnulation(a)}>Annuler</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
